import React, { useState, useEffect } from "react";
import { useInstantSearch } from "react-instantsearch";
import TriPar from "./TriPar";
import RangeSlider from "./RangeSlider";
import MultiLieuxFacet from "./MultiLieuxFacet";
import { OptionalRefinementList, OptionalAccordionFacet } from "./OptionalFacet";
import FondsTriStateFacet from "./FondsTriStateFacet";
import AccessToggle from "./AccessToggle";

const DOC_TYPE_LABELS = { k: "Photographie", v: "Film/Vidéo", s: "Audio" };
const extractIdRefLabel = (label) => (label && label.includes("|")) ? label.split("|")[1] : label;

export default function SidebarFacets() {
  const [photoChildTotal, setPhotoChildTotal] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Affichage mobile (≤ 800px) → facettes repliables
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 800px)").matches
  );
  const { results, indexUiState } = useInstantSearch();

  // Suit les changements de largeur (rotation, redimensionnement)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 800px)");
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  // Rafraîchir le total des sous-documents des photos à chaque recherche
  useEffect(() => {
    if (!results) return;
    let cancelled = false;

    const must = [];
    const mustNot = [];
    const rf = indexUiState.refinementList || {};
    for (const [attr, values] of Object.entries(rf)) {
      if (!values || values.length === 0) continue;
      for (const v of values) {
        if (typeof v === "string" && v.startsWith("NOT:")) {
          mustNot.push({ term: { [attr]: v.slice(4) } });
        } else {
          must.push({ term: { [attr]: v } });
        }
      }
    }
    const range = indexUiState.range || {};
    for (const [attr, bounds] of Object.entries(range)) {
      const r = {};
      if (bounds.min != null) r.gte = bounds.min;
      if (bounds.max != null) r.lte = bounds.max;
      if (Object.keys(r).length > 0) must.push({ range: { [attr]: r } });
    }

    const mustClauses = [];
    const queryText = indexUiState.query || "";
    if (queryText.trim()) {
      mustClauses.push({ multi_match: { query: queryText, fields: ["full_text", "titre", "cote.text", "auteur.text"] } });
    }

    const filterArr = [];
    if (must.length > 0) filterArr.push(...must);
    if (mustNot.length > 0) filterArr.push({ bool: { must_not: mustNot } });

    const boolQuery = {};
    if (mustClauses.length > 0) boolQuery.must = mustClauses;
    if (filterArr.length > 0) boolQuery.filter = filterArr;
    const queryClause = Object.keys(boolQuery).length > 0 ? { bool: boolQuery } : { match_all: {} };

    const body = {
      size: 0,
      query: queryClause,
      aggs: {
        photos: {
          filter: { term: { docType: "k" } },
          aggs: {
            children: {
              nested: { path: "docs" },
              aggs: { total: { value_count: { field: "docs.avdId" } } }
            }
          }
        }
      }
    };

    fetch("./search-proxy.php/memovs_av_v1/_search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(data => {
        if (!cancelled) {
          setPhotoChildTotal(data?.aggregations?.photos?.children?.total?.value ?? 0);
        }
      })
      .catch((err) => { console.error("Photo child count fetch failed:", err); });
    return () => { cancelled = true; };
  }, [results, indexUiState]);

  return (
    <div className="sui-layout-sidebar">

      {isMobile && (
        <button
          type="button"
          className="mobile-filters-toggle"
          onClick={() => setFiltersOpen((o) => !o)}
          aria-expanded={filtersOpen}
        >
          {filtersOpen ? "✕ Masquer les filtres" : "☰ Afficher les filtres"}
        </button>
      )}

      <div className={"sui-facets-content" + (filtersOpen ? " is-open" : "")}>
      {/* Les widgets restent MONTES en permanence : l'état d'URL (query + refinementList) en dépend.
          Sur mobile, c'est le CSS (.sui-facets-content / .is-open) qui replie le panneau. */}
      <TriPar />

      <AccessToggle />

      <OptionalRefinementList attribute="docType" title="Type de document" searchable={false} labelMap={DOC_TYPE_LABELS} formatLabel={(label) => {
        let display = DOC_TYPE_LABELS[label] || label;
        if (label === "k" && photoChildTotal != null) {
          display += ` (${photoChildTotal.toLocaleString("fr-CH")})`;
        }
        return display;
      }} />

      <div className="sui-facet">
        <legend className="sui-facet__title">Période</legend>
        <RangeSlider attribute="date" />
      </div>

      <MultiLieuxFacet attribute="lieu" searchable={true} showMore={true} limit={12} showMoreLimit={450} searchablePlaceholder="Filtrer les lieux..." />

      <FondsTriStateFacet attribute="fonds" searchable={true} limit={12} showMoreLimit={500} />

      <OptionalAccordionFacet attribute="auteur" title="Auteurs" searchable={true} limit={12} showMoreLimit={2400} />

      <OptionalAccordionFacet attribute="emission" title="Émissions" searchable={true} limit={12} showMoreLimit={400} />

      <OptionalAccordionFacet attribute="avmat" title="CDU" searchable={true} limit={10} showMoreLimit={600} />
      </div>

    </div>
  );
}
